import { LoadingOutlined } from '@ant-design/icons';
import { skipToken, useMutation, useQuery } from '@apollo/client/react';
import { Form } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { STATUSES } from '@guuru/chat-common';
import { botMessagesTypes } from '@guuru/chat-web';
import events from '@guuru/events-web';
import { SendButtonOutlined } from '@guuru/react-icon';
import { FormItem, SearchInput } from '../../SearchInput';
import ChatRenderText from '../Text';
import PROVIDE_USER_EMAIL from './mutations/ProvideUserEmail';
import GET_CHAT from './queries/GetChatSmsFallback';

const { waitFallbackRegistered } = botMessagesTypes;

type Status = keyof typeof STATUSES;

interface WaitFallbackProps {
  chatId: string;
  message: React.ComponentProps<typeof ChatRenderText>['message'] & {
    email: string;
  };
  readOnly?: boolean;
}

const WaitFallback = function ({
  chatId,
  message,
  readOnly = false,
}: WaitFallbackProps) {
  const { t } = useTranslation();
  const { email, text } = message;

  const [userEmail, setUserEmail] = useState<string>();
  const [form] = Form.useForm();
  const { data, loading } = useQuery(
    GET_CHAT,
    readOnly ? skipToken : { variables: { id: chatId } },
  );

  const { id, status } = data?.chat ?? {};

  const currentChatStatus = status as Status | undefined;

  const [provideUserEmail, { error, loading: isSaving }] =
    useMutation(PROVIDE_USER_EMAIL);

  const confirmedEmail = userEmail || email;
  const isEmailConfirmed = !isSaving && confirmedEmail;

  const handleSubmit = () => {
    form
      .validateFields()
      .then(async (values) => {
        try {
          await provideUserEmail({
            variables: { chatId: id!, email: values.email },
          });
          events.waitFallback(values.email);
          setUserEmail(values.email);
        } catch (_) {
          setUserEmail('');
        }
      })
      .catch((err) => err);
  };
  if (isEmailConfirmed) {
    const numberConfirmed = {
      ...message,
      i18n: email ? text! : t(waitFallbackRegistered, { email }),
    };
    return <ChatRenderText message={numberConfirmed} tooltip={false} />;
  }

  const disabled =
    readOnly
    || (!isEmailConfirmed
      && ![STATUSES.assigned, STATUSES.created].includes(currentChatStatus!));

  const Icon = isSaving ? LoadingOutlined : SendButtonOutlined;

  const validation = t('bot:::messageTypeContactValidationEmail');
  const placeholder = t('bot:::messageTypeContactMessageEmail');

  return (
    <ChatRenderText message={message} tooltip={false} loading={loading}>
      {!disabled && (
        <Form
          form={form}
          initialValues={{ email: '' }}
          className="message-options"
        >
          <FormItem
            name="email"
            help={error && validation}
            rules={[{ required: true, message: validation, type: 'email' }]}
            validateStatus={error ? 'error' : undefined}
          >
            <SearchInput
              placeholder={placeholder}
              enterButton={<Icon />}
              onSearch={handleSubmit}
              disabled={disabled || isSaving}
            />
          </FormItem>
        </Form>
      )}
    </ChatRenderText>
  );
};

export default WaitFallback;
